/**
 * Real-time analytics component with WebSocket integration
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  Badge,
  LinearProgress,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Wifi as ConnectedIcon,
  WifiOff as DisconnectedIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';

import { websocketService } from '../../services/websocketService';
import { analyticsService, DashboardSummary } from '../../services/analyticsService';

interface RealTimeMetrics {
  dashboard_summary?: DashboardSummary;
  active_conversations?: number;
  new_conversations_today?: number;
  performance_metrics?: {
    avg_response_time: number;
    avg_confidence: number;
    success_rate: number;
    total_messages_last_hour: number;
  };
  system_status?: {
    status: string;
    active_conversations: number;
    database_connected: boolean;
    websocket_connections: number;
  };
  timestamp?: string;
}

interface Alert {
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  metric_value?: number;
  threshold_value?: number;
  timestamp?: string;
}

const RealTimeAnalytics: React.FC = () => {
  const [metrics, setMetrics] = useState<RealTimeMetrics>({});
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<string>('disconnected');
  const [isRealTimeEnabled, setIsRealTimeEnabled] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);

  // Handle dashboard updates
  const handleDashboardUpdate = useCallback((data: any) => {
    setMetrics(prevMetrics => ({
      ...prevMetrics,
      ...data
    }));
    setLastUpdate(new Date());
  }, []);

  // Handle performance alerts
  const handlePerformanceAlert = useCallback((alertData: Alert) => {
    setAlerts(prevAlerts => {
      // Add timestamp if not present
      const alert = {
        ...alertData,
        timestamp: alertData.timestamp || new Date().toISOString()
      };
      
      // Keep only last 10 alerts
      const newAlerts = [alert, ...prevAlerts].slice(0, 10);
      return newAlerts;
    });
  }, []);

  // Handle system status updates
  const handleSystemStatus = useCallback((statusData: any) => {
    setMetrics(prevMetrics => ({
      ...prevMetrics,
      system_status: statusData
    }));
  }, []);

  // Initialize WebSocket connection and subscriptions
  useEffect(() => {
    if (!isRealTimeEnabled) {
      setConnectionStatus('disconnected');
      return;
    }

    let isMounted = true;
    const unsubscribeHandlers: Array<() => void> = [];

    const statusInterval = setInterval(() => {
      setConnectionStatus(websocketService.getConnectionStatus());
    }, 1000);

    const connectAndSubscribe = async () => {
      try {
        await websocketService.connect();
        if (!isMounted) return;

        unsubscribeHandlers.push(
          websocketService.subscribe('dashboard', handleDashboardUpdate),
          websocketService.subscribe('alerts', handlePerformanceAlert),
          websocketService.subscribe('system', handleSystemStatus)
        );
      } catch (error) {
        console.error('Failed to connect to WebSocket:', error);
        if (isMounted) {
          setConnectionStatus('error');
        }
      }
    };

    void connectAndSubscribe();

    return () => {
      isMounted = false;
      unsubscribeHandlers.forEach((unsubscribe) => unsubscribe());
      clearInterval(statusInterval);
    };
  }, [isRealTimeEnabled, handleDashboardUpdate, handlePerformanceAlert, handleSystemStatus]);

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        const summary = await analyticsService.getDashboardSummary();
        setMetrics({ dashboard_summary: summary });
        setLastUpdate(new Date());
      } catch (error) {
        console.error('Failed to load initial analytics data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Manual refresh
  const handleRefresh = async () => {
    try {
      setLoading(true);
      const summary = await analyticsService.getDashboardSummary();
      setMetrics(prevMetrics => ({
        ...prevMetrics,
        dashboard_summary: summary
      }));
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to refresh data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get connection status color and icon
  const getConnectionStatusDisplay = () => {
    switch (connectionStatus) {
      case 'connected':
        return { color: 'success', icon: <ConnectedIcon />, text: 'Conectado' };
      case 'connecting':
        return { color: 'warning', icon: <RefreshIcon />, text: 'Conectando...' };
      case 'disconnected':
        return { color: 'error', icon: <DisconnectedIcon />, text: 'Desconectado' };
      default:
        return { color: 'default', icon: <DisconnectedIcon />, text: 'Desconocido' };
    }
  };

  // Format percentage change
  const formatChange = (value: number) => {
    const sign = value >= 0 ? '+' : '';
    const color = value >= 0 ? 'success.main' : 'error.main';
    const icon = value >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />;
    
    return (
      <Box display="flex" alignItems="center" color={color}>
        {icon}
        <Typography variant="body2" sx={{ ml: 0.5 }}>
          {sign}{value.toFixed(1)}%
        </Typography>
      </Box>
    );
  };

  // Get alert severity color
  const getAlertSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const connectionDisplay = getConnectionStatusDisplay();

  return (
    <Box>
      {/* Header with connection status and controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          Análisis en Tiempo Real
        </Typography>
        
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={isRealTimeEnabled}
                onChange={(e) => setIsRealTimeEnabled(e.target.checked)}
              />
            }
            label="Tiempo Real"
          />
          
          <Chip
            icon={connectionDisplay.icon}
            label={connectionDisplay.text}
            color={connectionDisplay.color as any}
            variant="outlined"
          />
          
          <Tooltip title="Actualizar datos">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Loading indicator */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Last update info */}
      {lastUpdate && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Última actualización: {lastUpdate.toLocaleTimeString()}
        </Typography>
      )}

      {/* Real-time metrics cards */}
      <Grid container spacing={3}>
        {/* Dashboard Summary */}
        {metrics.dashboard_summary && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Conversaciones Hoy
                  </Typography>
                  <Typography variant="h4" component="div">
                    {metrics.dashboard_summary.total_conversations_today}
                  </Typography>
                  {formatChange(metrics.dashboard_summary.total_conversations_change)}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Conversaciones Activas
                  </Typography>
                  <Typography variant="h4" component="div">
                    {metrics.system_status?.active_conversations ??
                      metrics.active_conversations ??
                      metrics.dashboard_summary.active_conversations}
                  </Typography>
                  {formatChange(metrics.dashboard_summary.active_conversations_change)}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Leads Hoy
                  </Typography>
                  <Typography variant="h4" component="div">
                    {metrics.dashboard_summary.total_leads_today}
                  </Typography>
                  {formatChange(metrics.dashboard_summary.total_leads_change)}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Tasa de Conversión
                  </Typography>
                  <Typography variant="h4" component="div">
                    {metrics.dashboard_summary.conversion_rate_today.toFixed(1)}%
                  </Typography>
                  {formatChange(metrics.dashboard_summary.conversion_rate_change)}
                </CardContent>
              </Card>
            </Grid>
          </>
        )}

        {/* Performance Metrics */}
        {metrics.performance_metrics && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Tiempo de Respuesta
                  </Typography>
                  <Typography variant="h4" component="div">
                    {metrics.performance_metrics.avg_response_time.toFixed(2)}s
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Confianza Promedio
                  </Typography>
                  <Typography variant="h4" component="div">
                    {(metrics.performance_metrics.avg_confidence * 100).toFixed(1)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Tasa de Éxito
                  </Typography>
                  <Typography variant="h4" component="div">
                    {metrics.performance_metrics.success_rate.toFixed(1)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Mensajes (1h)
                  </Typography>
                  <Typography variant="h4" component="div">
                    {metrics.performance_metrics.total_messages_last_hour}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}

        {/* System Status */}
        {metrics.system_status && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Estado del Sistema
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box display="flex" alignItems="center">
                      <Typography variant="body2" sx={{ mr: 1 }}>
                        Estado:
                      </Typography>
                      <Chip
                        label={metrics.system_status.status}
                        color={metrics.system_status.status === 'healthy' ? 'success' : 'warning'}
                        size="small"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">
                      Conversaciones Activas: {metrics.system_status.active_conversations}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">
                      Base de Datos: {metrics.system_status.database_connected ? 'Conectada' : 'Desconectada'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">
                      Conexiones WS: {metrics.system_status.websocket_connections}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Alerts */}
        {alerts.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Badge badgeContent={alerts.length} color="error">
                    Alertas Recientes
                  </Badge>
                </Typography>
                <Box>
                  {alerts.slice(0, 5).map((alert, index) => (
                    <Alert
                      key={index}
                      severity={getAlertSeverityColor(alert.severity) as any}
                      sx={{ mb: 1 }}
                      icon={
                        alert.severity === 'critical' ? <ErrorIcon /> :
                        alert.severity === 'high' ? <WarningIcon /> :
                        <SuccessIcon />
                      }
                    >
                      <Typography variant="subtitle2">{alert.title}</Typography>
                      <Typography variant="body2">{alert.description}</Typography>
                      {alert.timestamp && (
                        <Typography variant="caption" color="text.secondary">
                          {new Date(alert.timestamp).toLocaleTimeString()}
                        </Typography>
                      )}
                    </Alert>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default RealTimeAnalytics;
